/* eslint-disable no-unused-vars */
import React, {useState, useEffect, memo, useCallback, useRef} from 'react';
import {
  Grid,
  Card,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Typography,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Checkbox,
  Tab,
  Tabs,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Add,
  Delete,
  DragIndicator,
  ArrowUpward,
  ArrowDownward,
  Save,
  CloudUpload,
  Close,
  ExpandMore,
  Visibility,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {useParams, useNavigate} from 'react-router-dom';
import {Block, Notify} from 'notiflix';
import {useForm, useFieldArray, Controller} from 'react-hook-form';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import {sectionTemplateApi} from 'api/sectionTemplateApi';
import {slotApi} from 'api/slotApi';
import {useCountry} from 'context/CountryContext';
import CountryDropdown from 'components/country/CountryDropdown';
import PageSelector from 'layouts/pages/PageSelector';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {ChromePicker} from 'react-color';
import NestedFieldBuilder from 'components/Cms/NestedFieldBuilders';
import FieldRenderer from 'components/Cms/FieldRenderer';
import TypeKeySelector from 'components/Cms/TypeKeySelector';

const FIELD_TYPES = [
  {
    value: 'text',
    label: 'Text (Short)',
    icon: '📝',
    category: 'basic',
    description: 'Single line text input',
  },
  {
    value: 'textarea',
    label: 'Text Area (Long)',
    icon: '📄',
    category: 'basic',
    description: 'Multi-line text input',
  },
  {
    value: 'wysiwyg',
    label: 'Rich Text Editor',
    icon: '🔤',
    category: 'basic',
    description: 'HTML formatted text',
  },
  {
    value: 'number',
    label: 'Number',
    icon: '🔢',
    category: 'basic',
    description: 'Numeric input with min/max',
  },
  {
    value: 'email',
    label: 'Email',
    icon: '📧',
    category: 'basic',
    description: 'Email address with validation',
  },
  {
    value: 'tel',
    label: 'Phone',
    icon: '📞',
    category: 'basic',
    description: 'Phone number input',
  },
  {
    value: 'url',
    label: 'URL',
    icon: '🔗',
    category: 'basic',
    description: 'Website URL with validation',
  },
  {
    value: 'image',
    label: 'Single Image',
    icon: '🖼️',
    category: 'media',
    description: 'Single image upload',
  },
  {
    value: 'gallery',
    label: 'Image Gallery',
    icon: '🖼️🖼️',
    category: 'media',
    description: 'Multiple images upload',
  },
  {
    value: 'video',
    label: 'Video',
    icon: '🎬',
    category: 'media',
    description: 'Video URL or embed',
  },
  {
    value: 'file',
    label: 'File Download',
    icon: '📎',
    category: 'media',
    description: 'File attachment',
  },
  {
    value: 'icon',
    label: 'Icon Picker',
    icon: '✨',
    category: 'media',
    description: 'Icon selection',
  },
  {
    value: 'select',
    label: 'Dropdown Select',
    icon: '⬇️',
    category: 'choice',
    description: 'Single selection from dropdown',
  },
  {
    value: 'radio',
    label: 'Radio Button',
    icon: '⚪',
    category: 'choice',
    description: 'Single selection from options',
  },
  {
    value: 'checkbox',
    label: 'Checkbox',
    icon: '✅',
    category: 'choice',
    description: 'Boolean true/false',
  },
  {
    value: 'multiselect',
    label: 'Multi Select',
    icon: '🔽',
    category: 'choice',
    description: 'Multiple selections',
  },
  {
    value: 'repeater',
    label: 'Repeater (Repeatable)',
    icon: '🔄',
    category: 'complex',
    description: 'Repeatable group of fields',
  },
  {
    value: 'group',
    label: 'Group (Nested)',
    icon: '📦',
    category: 'complex',
    description: 'Group of related fields',
  },
  {
    value: 'color',
    label: 'Color Picker',
    icon: '🎨',
    category: 'style',
    description: 'Color selection',
  },
  {
    value: 'link',
    label: 'Link/Button',
    icon: '🔗',
    category: 'interactive',
    description: 'Link with text and URL',
  },
  {
    value: 'date',
    label: 'Date Picker',
    icon: '📅',
    category: 'interactive',
    description: 'Date selection',
  },
];

const CATEGORIES = [
  'hero',
  'enterprise',
  'pricing_enterprise',
  'services_enterprise',
  'about_enterprise',
  'contact_enterprise',
  'services',
  'home_services',
  'home_cancel_list',
  'home_pricing',
  'home_logo_design',
  'home_stats',
  'home_problem_solution',
  'home_expandable',
  'home_industries',
  'home_live_feed',
  'home_our_process',
  'home_awards',
  'home_fit_finder',
  'home_why_tecnogex',
  'home_founder_message',
  'home_blog',
  'home_final_cta',
  'gallery',
  'testimonials',
  'faq',
  'contact',
  'about',
  'cta',
  'features',
  'pricing',
  'team',
  'blog',
  'logo_section',
  'success',
  'case_studies',
  'shop_banner',
  'shop_enterprise',
  'seasonal_banner',
  'why_choose_us',
  'client_dashboard',
  'transformation',
  'advantage',
  'process',
  'shop_inner_hero',
  'capabilities',
  'overview',
  'sectors',
  'solutions',
  'stats',
  'operations',
  'ecommerce_features',
  'matrix',
  'industry_enterprise',
  'banner',
  'industries',
  'every_day_challenges',
  'ecommerce',
  'portfolio',
  'approach',
  'advanced_features',
  'our_work',
  'our_values',
  'email_setup',
  'why_choose_us',
  'problem_solution',
  'mvp',
  'problem',
  'needs',
  'integrations',
  'design_category_box',
  'find_products',
  'service_benefits',
  'our_process_steps',
  'stats_highlights',
  'our_work',
  'marketing_platform',
  'valuepack_benefits',
  'valuepack_benefits2',
  'valuepack_outcomes',
  'valuepack_inclusions',
  'valuepack_popular',
  'valuepack_stat',
  'valuepack_savings_cta',
  'valuepack_plans_header',
  'valuepack_audience',
  'valuepack_addons',
  'valuepack_comparison',
  'valuepack_ai_process',
  'valuepack_what_you_get',
  'product_replace_hiring',
  'product_unlocks',
  'product_problem_solution',
  'product_honest_check',
  'product_city_cta',
  'product_switch_agency',
  'product_real_comparison',
  'custom',
  'section_header',
  'card_content',
  'benefits',
  'benefits_list',
  'limited_time_offer',
  'case_study_hero',
  'case_study_stats',
  'testimonials_profile_cards',
  'testimonials_section',
  'testimonials_wall_of_love',
  'case_study_testimonial',
  'why_tecnogex_for_dev',
  'contact_us',
  'legal_hero',
  'legal_content',
  'legal_cta',
  'privacy_policy',
  'terms_of_service',
  'cookie_policy',
  'refund_policy',
  'growth_section',
  'contact_us',
  'journey',
  'customer_profile_collection',
  'full_visibility',
  'choose_your_plan',
  'budget_quietly',
  'typical_agencies',
  'free_analysis',
  'card_logo',
  'industry_value_packs_header',
  'about_hero',
  'origin_story',
  'trust_strip',
  'about_timeline',
  'define_tecnogex',
  'how_its_built',
  'about_founder_message',
  'the_receipts',
  'keep_evolving',
  'our_locations',
  'about_final_cta',
  'shift',
  'real_problem',
  'free_strategy_session',
  'opportunity',
  'app_types',
  'explore_concept',
  'how_we_work',
  'upgrades',
  'we_can_do',
  'how_we_start',
  'single_point_contact',
  'building',
  'core_feature',
  'app_management',
  'build_for_every_device',
  'build_to_last',
  'why_tecnogex',
  'book_demo',
  'lets_build',
  'our_process',
  'growth_after_launch',
  'before_and_after',
  'industry_value_packs_header',
  'saas_model',
  'ux_and_design',
  'saas_shift',
  'saas_build',
  'honest_talk',
  'trending_right_now',
  'need_something_more',
  'category_pain_points',
  'category_featured_picks',
  'category_why_tecnogex',
  'category_combos',
  'category_dashboard',
  'category_cta',
  'shop_cta',
  'shop_warning_bar',
  'shop_home_sections',
  'go_beyond_launch',
  'launch_preparation',
  'value_packs_hero',
  'value_packs_popular_header',
  'value_packs_list_header',
  'strategy_callout',
  'value_packs_importance',
  'value_packs_trending',
  'value_packs_why_us',
  'offer_banner',
  'still_deciding',
  'seo_faq'
];

const OptionBuilder = ({
  fieldIndex,
  control,
  register,
  setValue,
  getValues,
}) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const currentOptions = getValues(`${fieldIndex}.options`) || [];
    setOptions(currentOptions);
  }, [getValues, fieldIndex]);

  const addOption = () => {
    const newOptions = [...options, {value: '', label: '', default: false}];
    setOptions(newOptions);
    setValue(`${fieldIndex}.options`, newOptions);
  };

  const updateOption = (index, key, value) => {
    const updated = [...options];
    updated[index][key] = value;
    setOptions(updated);
    setValue(`${fieldIndex}.options`, updated);
  };

  const removeOption = index => {
    const updated = options.filter((_, i) => i !== index);
    setOptions(updated);
    setValue(`${fieldIndex}.options`, updated);
  };

  const setDefaultOption = index => {
    const updated = options.map((opt, i) => ({
      ...opt,
      default: i === index,
    }));
    setOptions(updated);
    setValue(`${fieldIndex}.options`, updated);
  };

  return (
    <Box mt={2}>
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        gutterBottom>
        Options
      </Typography>

      {options.map((option, optIndex) => (
        <Paper key={optIndex} sx={{p: 1, mb: 1, bgcolor: 'grey.50'}}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                key={`option-value-${fieldIndex}-${optIndex}`}
                fullWidth
                size="small"
                variant="outlined"
                label="Value"
                value={option.value}
                onChange={e => updateOption(optIndex, 'value', e.target.value)}
                placeholder="option_value"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                key={`option-label-${fieldIndex}-${optIndex}`}
                fullWidth
                size="small"
                variant="outlined"
                label="Label"
                value={option.label}
                onChange={e => updateOption(optIndex, 'label', e.target.value)}
                placeholder="Display Label"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={option.default || false}
                    onChange={e => {
                      if (e.target.checked) {
                        setDefaultOption(optIndex);
                      } else {
                        updateOption(optIndex, 'default', false);
                      }
                    }}
                    size="small"
                  />
                }
                label="Default"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <IconButton
                size="small"
                color="error"
                onClick={() => removeOption(optIndex)}>
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}

      <Button size="small" startIcon={<Add />} onClick={addOption} sx={{mt: 1}}>
        Add Option
      </Button>
    </Box>
  );
};

const FieldBuilderItem = memo(
  ({
    field,
    index,
    control,
    register,
    errors,
    setValue,
    getValues,
    watch,
    move,
    remove,
    fields,
    fieldTypeCategory,
    dragHandleProps,
  }) => {
    const typeRef = useRef(field.type || 'text');
    const watchedType = watch(`fieldDefinitions.${index}.type`);

    if (watchedType && watchedType !== typeRef.current) {
      typeRef.current = watchedType;
    }

    // Memoize handlers
    const handleTypeChange = useCallback(
      (newType, onChange) => {
        onChange(newType);

        // Initialize fields array for group/repeater
        if (newType === 'group' || newType === 'repeater') {
          const currentFields = getValues(`fieldDefinitions.${index}.fields`);
          if (!currentFields || currentFields.length === 0) {
            setValue(`fieldDefinitions.${index}.fields`, []);
          }
        }
      },
      [index, getValues, setValue],
    );

    // Memoize move handlers
    const handleMoveUp = useCallback(() => {
      if (index > 0) move(index, index - 1);
    }, [index, move]);

    const handleMoveDown = useCallback(() => {
      if (index < fields.length - 1) move(index, index + 1);
    }, [index, fields.length, move]);

    const handleRemove = useCallback(() => {
      remove(index);
    }, [index, remove]);

    return (
      <Paper
        sx={{
          mb: 2,
          p: 2,
          position: 'relative',
          borderLeft: field.required ? '4px solid' : 'none',
          borderLeftColor: 'error.main',
          '& .MuiOutlinedInput-root': {
            position: 'relative',
            '& > .MuiOutlinedInput-notchedOutline': {
              transition: 'border-color 0.2s ease',
            },
          },
          '& .MuiInputLabel-root': {
            position: 'absolute',
            left: 0,
            top: 0,
            pointerEvents: 'none',
            transformOrigin: 'top left',
            // Force the label to stay in the correct position
            transform: 'translate(14px, -9px) scale(0.75)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
            '&.MuiInputLabel-animated': {
              transition:
                'color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            },
          },
        }}>
        <Grid container spacing={2} pr={6}>
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Field Key"
              {...register(`fieldDefinitions.${index}.key`, {
                required: 'Field key is required',
                pattern: {
                  value: /^[a-z0-9_]+$/,
                  message: 'Only lowercase letters, numbers, underscores',
                },
              })}
              error={!!errors.fieldDefinitions?.[index]?.key}
              helperText={errors.fieldDefinitions?.[index]?.key?.message}
              placeholder="unique_key"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Field Label"
              {...register(`fieldDefinitions.${index}.label`, {
                required: 'Field label is required',
              })}
              error={!!errors.fieldDefinitions?.[index]?.label}
              helperText={errors.fieldDefinitions?.[index]?.label?.message}
              placeholder="Display Label"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl
              fullWidth
              size="small"
              error={!!errors.fieldDefinitions?.[index]?.type}>
              <InputLabel>Type</InputLabel>
              <Controller
                name={`fieldDefinitions.${index}.type`}
                control={control}
                defaultValue={field.type || 'text'}
                render={({field: {onChange, value}}) => (
                  <Select
                    label="Type"
                    value={value || 'text'}
                    onChange={e => handleTypeChange(e.target.value, onChange)}>
                    {FIELD_TYPES.filter(
                      t =>
                        fieldTypeCategory === 'all' ||
                        t.category === fieldTypeCategory,
                    ).map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        <Tooltip title={type.description} placement="right">
                          <span>
                            {type.icon} {type.label}
                          </span>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={6} md={1.5}>
            <FormControlLabel
              control={
                <Controller
                  name={`fieldDefinitions.${index}.required`}
                  control={control}
                  defaultValue={false}
                  render={({field: {onChange, value}}) => (
                    <Switch
                      checked={value || false}
                      onChange={e => onChange(e.target.checked)}
                      size="small"
                      color="error"
                    />
                  )}
                />
              }
              label="Required"
            />
          </Grid>

          <Grid item xs={6} md={1.5}>
            <FormControlLabel
              control={
                <Controller
                  name={`fieldDefinitions.${index}.hidden`}
                  control={control}
                  defaultValue={false}
                  render={({field: {onChange, value}}) => (
                    <Switch
                      checked={value || false}
                      onChange={e => onChange(e.target.checked)}
                      size="small"
                    />
                  )}
                />
              }
              label="Hidden"
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <IconButton
                size="small"
                sx={{cursor: 'grab'}}
                {...dragHandleProps}>
                <DragIndicator fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleMoveUp}
                disabled={index === 0}>
                <ArrowUpward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleMoveDown}
                disabled={index === fields.length - 1}>
                <ArrowDownward fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={handleRemove}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* Use typeRef.current for conditional rendering */}
        <Box sx={{mt: 2, pl: 2}}>
          {['text', 'textarea', 'email', 'tel', 'url'].includes(
            typeRef.current,
          ) && (
            <Grid container spacing={2} sx={{mb: 2}}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Placeholder"
                  {...register(`fieldDefinitions.${index}.placeholder`)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Default Value"
                  {...register(`fieldDefinitions.${index}.defaultValue`)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          )}

          {typeRef.current === 'number' && (
            <Grid container spacing={2} sx={{mb: 2}}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  variant="outlined"
                  label="Min"
                  {...register(`fieldDefinitions.${index}.min`)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  variant="outlined"
                  label="Max"
                  {...register(`fieldDefinitions.${index}.max`)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  variant="outlined"
                  label="Step"
                  {...register(`fieldDefinitions.${index}.step`)}
                  defaultValue={1}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          )}

          {typeRef.current === 'repeater' && (
            <Grid container spacing={2} sx={{mb: 2}}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  variant="outlined"
                  label="Min Items"
                  {...register(`fieldDefinitions.${index}.minItems`)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  variant="outlined"
                  label="Max Items"
                  {...register(`fieldDefinitions.${index}.maxItems`)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Controller
                      name={`fieldDefinitions.${index}.sortable`}
                      control={control}
                      defaultValue={true}
                      render={({field: {onChange, value}}) => (
                        <Switch
                          checked={value !== false}
                          onChange={e => onChange(e.target.checked)}
                          size="small"
                        />
                      )}
                    />
                  }
                  label="Sortable"
                />
              </Grid>
            </Grid>
          )}

          {(typeRef.current === 'select' ||
            typeRef.current === 'radio' ||
            typeRef.current === 'multiselect') && (
            <OptionBuilder
              fieldIndex={`fieldDefinitions.${index}`}
              control={control}
              register={register}
              setValue={setValue}
              getValues={getValues}
            />
          )}
        </Box>

        {/* Nested fields for group/repeater - use typeRef.current */}
        {(typeRef.current === 'repeater' || typeRef.current === 'group') && (
          <Box sx={{mt: 2}}>
            <NestedFieldBuilder
              fieldPath={`fieldDefinitions.${index}`}
              nestLevel={1}
              maxDepth={3}
              control={control}
              register={register}
              setValue={setValue}
              getValues={getValues}
              errors={errors}
            />
          </Box>
        )}
      </Paper>
    );
  },
  // (prevProps, nextProps) => {
  //   if (prevProps.index !== nextProps.index) return false;
  //   if (prevProps.fieldTypeCategory !== nextProps.fieldTypeCategory)
  //     return false;
  //   if (prevProps.fields.length !== nextProps.fields.length) return false;

  //   // Check if field properties that affect UI have changed
  //   const prevField = prevProps.field;
  //   const nextField = nextProps.field;

  //   if (prevField.required !== nextField.required) return false;
  //   if (prevField.hidden !== nextField.hidden) return false;
  //   if (prevField.key !== nextField.key) return false;
  //   if (prevField.label !== nextField.label) return false;

  //   // Check errors (but only if they exist)
  //   const prevError = prevProps.errors?.fieldDefinitions?.[prevProps.index];
  //   const nextError = nextProps.errors?.fieldDefinitions?.[nextProps.index];
  //   if (prevError !== nextError) return false;

  //   // Don't re-render on scroll or other form changes
  //   return true;
  // },

  (prevProps, nextProps) => {
    return (
      prevProps.index === nextProps.index &&
      prevProps.fields.length === nextProps.fields.length &&
      prevProps.fieldTypeCategory === nextProps.fieldTypeCategory
    );
  },
);

FieldBuilderItem.displayName = 'FieldBuilderItem';

function SectionTemplateForm() {
  const {id} = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'new';

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(isEditing);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [fieldTypeCategory, setFieldTypeCategory] = useState('all');
  const [categoryManuallyChanged, setCategoryManuallyChanged] = useState(false);

  const [imageFiles, setImageFiles] = useState({});
  const [galleryFiles, setGalleryFiles] = useState({});

  // ===== NEW: Slot states =====
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);

  const {
    countries,
    loading: countriesLoading,
    getDefaultCountry,
    getCountryByCode,
  } = useCountry();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    trigger,
    formState: {errors, isDirty},
  } = useForm({
    defaultValues: {
      name: '',
      typeKey: '',
      description: '',
      category: '',
      countryId: '',
      slotId: '',
      thumbnailUrl: '',
      galleryUrls: [],
      fieldDefinitions: [],
      defaultStructure: {},
      settingsSchema: {
        padding: {top: 'medium', bottom: 'medium'},
        margin: {top: 'none', bottom: 'none'},
        bgColor: '#ffffff',
        textColor: '#000000',
        textAlign: 'left',
        containerWidth: 'full',
        maxWidth: '1200px',
        borderRadius: 'none',
        shadow: 'none',
      },
      isSystem: false,
      isLocked: false,
      version: 1,
      scope: 'global',
      customTypeKey: '',
      allowedPageIds: [],
    },
    shouldUseNativeValidation: false,
  });

  const currentScope = watch('scope');

  const {fields, append, remove, move} = useFieldArray({
    control,
    name: 'fieldDefinitions',
    keyName: 'fieldArrayId',
  });

  // ===== NEW: Fetch slots function =====
  const fetchSlots = async () => {
    try {
      setLoadingSlots(true);
      const countryId = getValues('countryId');
      let countryCode = null;

      if (countryId) {
        const country = countries.find(c => c.id === countryId);
        countryCode = country?.countryCode;
      }

      const response = await slotApi.getActiveSlots(countryCode);
      setSlots(response?.data?.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      Notify.failure('Error loading slots', {
        timeout: 3000,
        position: 'center-top',
      });
    } finally {
      setLoadingSlots(false);
    }
  };

  // ===== NEW: Fetch slots when country changes =====
  useEffect(() => {
    if (countries.length > 0) {
      fetchSlots();
    }
  }, [watch('countryId'), countries]);

  useEffect(() => {
    if (!isEditing && countries.length > 0 && !getValues('countryId')) {
      const defaultCountry = getCountryByCode('IN') || getDefaultCountry();
      if (defaultCountry) {
        setValue('countryId', defaultCountry.id);
      }
    }
  }, [countries, isEditing]);

  useEffect(() => {
    if (isEditing) {
      fetchTemplate();
    }

    return () => {
      if (thumbnailPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(thumbnailPreview);
      }
      galleryPreviews.forEach(preview => {
        if (preview?.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [id]);

  useEffect(() => {
    if (loading || countriesLoading) {
      Block.circle('#template-form', 'Loading...');
    } else {
      Block.remove('#template-form');
    }
  }, [loading, countriesLoading]);

  // ===== UPDATED: fetchTemplate with slotId =====
  const fetchTemplate = async () => {
    try {
      const response = await sectionTemplateApi.getTemplateById(id);
      const templateData = sectionTemplateApi.transformTemplateDataForForm(
        response.template,
      );

      Object.keys(templateData).forEach(key => {
        setValue(key, templateData[key]);
      });

      if (templateData.scope === 'global') {
        setValue('allowedPageIds', []);
      }

      if (templateData.thumbnailUrl) {
        setThumbnailPreview(templateData.thumbnailUrl);
      }

      if (templateData.galleryUrls?.length > 0) {
        setGalleryPreviews(templateData.galleryUrls);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      Notify.failure(error.message || 'Error loading template', {
        timeout: 3000,
        position: 'center-top',
      });
      navigate('/section-templates');
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailChange = e => {
    const file = e.target.files[0];
    if (file) {
      if (thumbnailPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(thumbnailPreview);
      }
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const handleImageFileChange = (fieldKey, device, file) => {
    setImageFiles(prev => ({
      ...prev,
      [fieldKey]: {
        ...(prev[fieldKey] || {}),
        [device]: file,
      },
    }));
  };

  const handleGalleryFilesChange = (fieldKey, files) => {
    setGalleryFiles(prev => ({
      ...prev,
      [fieldKey]: files,
    }));
  };

  const removeGalleryImage = index => {
    if (galleryPreviews[index]?.startsWith('blob:')) {
      URL.revokeObjectURL(galleryPreviews[index]);
    }
    setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const addField = () => {
    append({
      id: `field_${Date.now()}_${Math.random()}_${fields.length}`,
      key: '',
      label: '',
      type: 'text',
      placeholder: '',
      defaultValue: '',
      required: false,
      hidden: false,
      options: [],
      fields: [],
      validation: {},
      condition: null,
      helpText: '',
      order: fields.length,
    });
  };

  const handleDragEnd = result => {
    if (!result.destination) return;
    move(result.source.index, result.destination.index);
  };

  const previewStructure = () => {
    const data = getValues();
    console.log(
      'Current Structure:',
      JSON.stringify(data.fieldDefinitions, null, 2),
    );
    alert('Check console for JSON structure');
  };

  const validateTemplate = data => {
    const errors = [];

    // Keys only need to be unique among siblings (within the same group/repeater),
    // not across the whole template — a fresh Set per level lets different
    // groups/repeaters reuse field keys like "title" independently.
    const checkDuplicateKeys = fields => {
      const siblingKeys = new Set();
      fields?.forEach(field => {
        if (field.key) {
          if (siblingKeys.has(field.key)) {
            errors.push(`Duplicate field key: ${field.key}`);
          }
          siblingKeys.add(field.key);
        }

        if (field.fields?.length > 0) {
          checkDuplicateKeys(field.fields);
        }
      });
    };
    checkDuplicateKeys(data.fieldDefinitions);

    if (data.scope === 'page-specific') {
      const selectedPages = Array.isArray(data.allowedPageIds)
        ? data.allowedPageIds.filter(Boolean)
        : [];
      if (selectedPages.length === 0) {
        errors.push(
          'Page-specific templates must have at least one page selected',
        );
      }
    }

    data.fieldDefinitions?.forEach((field, index) => {
      if (!field.key) errors.push(`Field ${index + 1}: Missing key`);
      if (!field.label) errors.push(`Field ${index + 1}: Missing label`);

      const checkNestedFields = (nestedFields, parentPath = '') => {
        nestedFields?.forEach((nestedField, nestedIndex) => {
          if (!nestedField.key) {
            errors.push(`${parentPath} Field ${nestedIndex + 1}: Missing key`);
          }
          if (!nestedField.label) {
            errors.push(
              `${parentPath} Field ${nestedIndex + 1}: Missing label`,
            );
          }

          if (nestedField.fields?.length > 0) {
            checkNestedFields(
              nestedField.fields,
              `${parentPath} > ${nestedField.label || 'unnamed'}`,
            );
          }
        });
      };

      if (field.fields?.length > 0) {
        checkNestedFields(field.fields, field.label || `Field ${index + 1}`);
      }
    });

    return errors;
  };

  const mergeFilesIntoStructure = (
    structure,
    fieldDefs,
    pathPrefix,
    imageFilesMap,
    galleryFilesMap,
  ) => {
    if (!structure || !fieldDefs) return structure;

    const fieldMap = {};
    fieldDefs.forEach(fd => {
      fieldMap[fd.key] = fd;
    });

    const result = Array.isArray(structure) ? [...structure] : {...structure};

    Object.entries(result).forEach(([key, value]) => {
      const fieldDef = fieldMap[key];
      const flatKey = pathPrefix ? `${pathPrefix}_${key}` : key;

      if (value === null || value === undefined) return;

      // --- IMAGE field ---
      if (
        fieldDef?.type === 'image' &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        const filesForThisField = imageFilesMap[flatKey];
        const merged = {};

        Object.entries(value).forEach(([device, val]) => {
          // If we have a File from imageFiles for this device, use it
          if (filesForThisField?.[device] instanceof File) {
            merged[device] = filesForThisField[device];
          }
          // If the value itself is already a File (set by DeviceImageUploader onChange), keep it
          else if (val instanceof File) {
            merged[device] = val;
          }
          // If it's a valid server URL (not blob), keep it
          else if (
            typeof val === 'string' &&
            val !== '' &&
            !val.startsWith('blob:')
          ) {
            merged[device] = val;
          }
          // blob URLs get dropped — they'll be replaced by File from imageFiles
        });

        result[key] = merged;
        return;
      }

      // --- GALLERY field ---
      if (fieldDef?.type === 'gallery' && Array.isArray(value)) {
        const filesForThisField = galleryFilesMap[flatKey];
        if (filesForThisField) {
          const existingUrls = value.filter(
            item =>
              item &&
              typeof item === 'object' &&
              Object.values(item).some(
                v =>
                  typeof v === 'string' &&
                  !v.startsWith('blob:') &&
                  !v.startsWith('__file__:') &&
                  v !== '',
              ),
          );

          const newFileEntries = [];
          Object.entries(filesForThisField).forEach(([device, fileList]) => {
            if (Array.isArray(fileList)) {
              fileList.forEach(file => {
                if (file instanceof File) {
                  newFileEntries.push({[device]: file});
                }
              });
            }
          });

          result[key] = [...existingUrls, ...newFileEntries];
        }
        return;
      }

      // --- REPEATER field ---
      if (fieldDef?.type === 'repeater' && Array.isArray(value)) {
        result[key] = value.map((item, index) => {
          if (item && typeof item === 'object') {
            const itemPrefix = `${flatKey}_${index}`;
            return mergeFilesIntoStructure(
              item,
              fieldDef.fields || [],
              itemPrefix,
              imageFilesMap,
              galleryFilesMap,
            );
          }
          return item;
        });
        return;
      }

      // --- GROUP field ---
      if (
        fieldDef?.type === 'group' &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        result[key] = mergeFilesIntoStructure(
          value,
          fieldDef.fields || [],
          flatKey,
          imageFilesMap,
          galleryFilesMap,
        );
        return;
      }
    });

    return result;
  };

  const removeFlatFileKeys = (
    defaultStructure,
    imageFilesMap,
    galleryFilesMap,
  ) => {
    const cleaned = {...defaultStructure};

    // Remove all keys that exist in imageFiles (flat keys)
    Object.keys(imageFilesMap).forEach(flatKey => {
      if (cleaned.hasOwnProperty(flatKey)) {
        delete cleaned[flatKey];
      }
    });

    // Remove all keys that exist in galleryFiles (flat keys)
    Object.keys(galleryFilesMap).forEach(flatKey => {
      if (cleaned.hasOwnProperty(flatKey)) {
        delete cleaned[flatKey];
      }
    });

    return cleaned;
  };

  const onSubmit = async data => {
    try {
      console.log('raw data : ', data);
      const validationErrors = validateTemplate(data);
      if (validationErrors.length > 0) {
        Notify.failure('Validation Errors:\n' + validationErrors.join('\n'), {
          timeout: 5000,
          position: 'center-top',
        });
        return;
      }

      if (!data.countryId) throw new Error('Please select a country');
      if (!data.category) throw new Error('Please select a category');
      if (!data.typeKey) throw new Error('Please select a type key');

      // that react-hook-form may have injected
      const cleanedDefaultStructure = cleanDefaultStructure(
        data.defaultStructure,
        data.fieldDefinitions,
      );

      let apiData = {
        ...data,
        defaultStructure: cleanedDefaultStructure,
        thumbnailFile,
        thumbnailUrl:
          data.thumbnailUrl && !data.thumbnailUrl.startsWith('blob:')
            ? data.thumbnailUrl
            : undefined,
        // Pass file maps separately — API layer will append to FormData
        imageFiles,
        galleryFiles,
      };

      if (data.scope === 'global') {
        delete apiData.allowedPageIds;
      } else if (data.scope === 'page-specific') {
        apiData.allowedPageIds = Array.isArray(data.allowedPageIds)
          ? data.allowedPageIds.filter(Boolean)
          : [];
        if (apiData.allowedPageIds.length === 0) {
          throw new Error(
            'Page-specific templates must have at least one page selected',
          );
        }
      }

      Object.keys(apiData).forEach(
        key => apiData[key] === undefined && delete apiData[key],
      );

      Block.circle('#template-form', 'Saving Template...');

      let response;
      if (isEditing) {
        response = await sectionTemplateApi.updateTemplate(id, apiData);
      } else {
        response = await sectionTemplateApi.createTemplate(apiData);
      }

      Block.remove('#template-form');
      Notify.success(
        isEditing
          ? 'Template updated successfully!'
          : 'Template created successfully!',
        {
          timeout: 3000,
          position: 'center-top',
        },
      );
      navigate('/section-templates');
    } catch (error) {
      console.error('Save error:', error);
      Block.remove('#template-form');
      Notify.failure(error.message || 'Error saving template', {
        timeout: 3000,
        position: 'center-top',
      });
    }
  };

  const cleanDefaultStructure = (structure, fieldDefinitions) => {
    if (!structure || typeof structure !== 'object') return structure;

    const fieldMap = {};
    if (Array.isArray(fieldDefinitions)) {
      fieldDefinitions.forEach(fd => {
        fieldMap[fd.key] = fd;
      });
    }

    const cleaned = Array.isArray(structure) ? [...structure] : {...structure};

    const validKeys = new Set(Object.keys(fieldMap));
    Object.keys(cleaned).forEach(key => {
      if (!validKeys.has(key) && key.includes('_')) {
        // This is likely a flat file key injected by react-hook-form
        delete cleaned[key];
      }
    });

    Object.entries(cleaned).forEach(([key, value]) => {
      const fieldDef = fieldMap[key];

      if (value === null || value === undefined) return;

      if (
        fieldDef?.type === 'image' &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        const cleanedImage = {};
        Object.entries(value).forEach(([device, val]) => {
          if (
            typeof val === 'string' &&
            val !== '' &&
            !val.startsWith('blob:')
          ) {
            cleanedImage[device] = val;
          }
          // Drop File objects and blob URLs — files are handled separately
        });
        cleaned[key] = cleanedImage;
        return;
      }

      // Gallery field — remove blob URLs and File objects
      if (fieldDef?.type === 'gallery' && Array.isArray(value)) {
        cleaned[key] = value.filter(item => {
          if (!item || typeof item !== 'object') return false;
          // Keep items that have at least one valid server URL
          return Object.values(item).some(
            v => typeof v === 'string' && v !== '' && !v.startsWith('blob:'),
          );
        });
        return;
      }

      // Repeater — recurse into each item
      if (fieldDef?.type === 'repeater' && Array.isArray(value)) {
        cleaned[key] = value.map(item => {
          if (item && typeof item === 'object') {
            return cleanDefaultStructure(item, fieldDef.fields || []);
          }
          return item;
        });
        return;
      }

      // Group — recurse
      if (
        fieldDef?.type === 'group' &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        cleaned[key] = cleanDefaultStructure(value, fieldDef.fields || []);
        return;
      }
    });

    return cleaned;
  };

  const renderFieldBuilder = () => (
    <MDBox
      sx={{
        '& .MuiPaper-root': {
          // Ensure Paper components don't have stacking context issues
          transform: 'translateZ(0)',
        },
        '& .MuiInputBase-root': {
          // Prevent label from being cut off
          overflow: 'visible',
        },
      }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <MDTypography variant="h6">Field Definitions</MDTypography>
          <Chip label={`${fields.length} fields`} color="info" size="small" />
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={previewStructure}>
            Preview JSON
          </Button>
        </Box>
        <MDButton
          variant="gradient"
          color="info"
          onClick={addField}
          size="small"
          startIcon={<Add />}>
          Add Field
        </MDButton>
      </Box>

      <Box sx={{mb: 3}}>
        <FormControl size="small" sx={{minWidth: 200}}>
          <Select
            value={fieldTypeCategory}
            onChange={e => setFieldTypeCategory(e.target.value)}
            displayEmpty>
            <MenuItem value="all">All Field Types</MenuItem>
            <MenuItem value="basic">Basic Fields</MenuItem>
            <MenuItem value="media">Media Fields</MenuItem>
            <MenuItem value="choice">Choice Fields</MenuItem>
            <MenuItem value="complex">Complex Fields</MenuItem>
            <MenuItem value="style">Style Fields</MenuItem>
            <MenuItem value="layout">Layout Fields</MenuItem>
            <MenuItem value="interactive">Interactive Fields</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {fields.length === 0 ? (
        <Paper sx={{p: 4, textAlign: 'center', bgcolor: 'grey.50'}}>
          <MDTypography color="text" gutterBottom>
            No fields added yet.
          </MDTypography>
          <MDTypography
            variant="caption"
            color="textSecondary"
            display="block"
            gutterBottom>
            Start building your template by adding fields below.
          </MDTypography>
        </Paper>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="fields">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {fields.map((field, index) => (
                  <Draggable
                    key={field.fieldArrayId}
                    draggableId={field.fieldArrayId}
                    index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          // Prevent overflow issues during drag
                          willChange: 'transform',
                        }}>
                        <FieldBuilderItem
                          field={field}
                          index={index}
                          control={control}
                          register={register}
                          errors={errors}
                          setValue={setValue}
                          getValues={getValues}
                          watch={watch}
                          move={move}
                          remove={remove}
                          fields={fields}
                          fieldTypeCategory={fieldTypeCategory}
                          dragHandleProps={provided.dragHandleProps}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </MDBox>
  );

  const renderDefaultContent = () => {
    const currentFields = watch('fieldDefinitions');

    if (!currentFields || currentFields?.length === 0) {
      return (
        <Paper sx={{p: 4, textAlign: 'center', bgcolor: 'grey.50'}}>
          <MDTypography color="text" gutterBottom>
            No fields defined yet.
          </MDTypography>
          <MDTypography variant="caption" color="textSecondary" display="block">
            Please define fields in the "Build Fields" step first.
          </MDTypography>
          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            sx={{mt: 2}}>
            Go to Build Fields
          </Button>
        </Paper>
      );
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="info" sx={{mb: 3}}>
            Set default content for this template. This content will be
            automatically applied when this template is added to any page. You
            can override it later per page.
          </Alert>
        </Grid>

        {currentFields.map(field => (
          <Grid
            item
            xs={12}
            md={field.type === 'wysiwyg' ? 12 : 12}
            key={field.key}>
            <FieldRenderer
              field={field}
              control={control}
              register={register}
              watch={watch}
              setValue={setValue}
              errors={errors}
              namePrefix="defaultStructure"
              imageFiles={imageFiles}
              onImageFileChange={handleImageFileChange}
              galleryFiles={galleryFiles}
              onGalleryFilesChange={handleGalleryFilesChange}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const steps = [
    'Basic Info',
    'Build Fields',
    'Default Content',
    'Settings & Validation',
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3} id="template-form">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info">
                <MDTypography variant="h6" color="white">
                  {isEditing
                    ? 'Edit Section Template'
                    : 'Create New Section Template'}
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <Stepper activeStep={activeStep} sx={{mb: 4}}>
                  {steps.map(label => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <form noValidate>
                  {/* Step 0: Basic Info */}
                  {activeStep === 0 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Template Scope</InputLabel>
                          <Controller
                            name="scope"
                            control={control}
                            defaultValue="global"
                            render={({field}) => (
                              <Select
                                {...field}
                                label="Template Scope"
                                onChange={e => {
                                  field.onChange(e.target.value);
                                  if (e.target.value === 'global') {
                                    setValue('typeKey', '');
                                    setValue('customTypeKey', '');
                                    setValue('allowedPageIds', []);
                                  }
                                }}>
                                <MenuItem value="global">
                                  🌍 Global Template (Available on all pages)
                                </MenuItem>
                                <MenuItem value="page-specific">
                                  📄 Page-Specific Template
                                </MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      {currentScope === 'page-specific' && (
                        <Grid item xs={12}>
                          <Typography
                            variant="subtitle1"
                            gutterBottom
                            // sx={{mt: 2}}
                          >
                            Specific Page Selection
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                            gutterBottom>
                            Select at least one page this template can be used on
                          </Typography>
                          <Controller
                            name="allowedPageIds"
                            control={control}
                            defaultValue={[]}
                            rules={{
                              validate: value =>
                                (Array.isArray(value) && value.length > 0) ||
                                'Select at least one page',
                            }}
                            render={({field, fieldState}) => (
                              <FormControl
                                fullWidth
                                error={!!fieldState.error}>
                                <PageSelector
                                  value={field.value}
                                  onChange={field.onChange}
                                  countryId={watch('countryId')}
                                  multiple
                                />
                                {fieldState.error && (
                                  <FormHelperText>
                                    {fieldState.error.message}
                                  </FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>
                      )}

                      <Box sx={{display: 'none'}}>
                        <Controller
                          name="countryId"
                          control={control}
                          rules={{required: 'Country is required'}}
                          render={({field}) => (
                            <CountryDropdown
                              value={field.value}
                              onChange={field.onChange}
                              label="Country *"
                              required={true}
                              error={!!errors.countryId}
                              helperText={errors.countryId?.message}
                              autoSelectDefault={false}
                            />
                          )}
                        />
                      </Box>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Template Name *"
                          {...register('name', {
                            required: 'Template name is required',
                          })}
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          placeholder="e.g., Hero Section"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors.category}>
                          <InputLabel required>Category *</InputLabel>
                          <Controller
                            name="category"
                            control={control}
                            rules={{required: 'Category is required'}}
                            render={({field}) => (
                              <Select
                                {...field}
                                label="Category *"
                                onChange={e => {
                                  field.onChange(e.target.value);
                                  setCategoryManuallyChanged(true);
                                  setValue('typeKey', '');
                                }}>
                                {CATEGORIES.map(cat => (
                                  <MenuItem
                                    key={cat}
                                    value={cat}
                                    sx={{textTransform: 'capitalize'}}>
                                    {cat}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          {errors.category && (
                            <FormHelperText>
                              {errors.category.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="typeKey"
                          control={control}
                          rules={{required: 'Type key is required'}}
                          render={({field, fieldState}) => (
                            <TypeKeySelector
                              value={field.value}
                              onChange={field.onChange}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              category={watch('category')}
                              disabled={!watch('category')}
                              readOnly={false}
                              scope={watch('scope')}
                              customTypeKey={watch('customTypeKey')}
                              onCustomTypeKeyChange={key =>
                                setValue('customTypeKey', key)
                              }
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Description"
                          {...register('description')}
                          placeholder="Describe what this section is used for..."
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDButton
                          variant="outlined"
                          color="info"
                          component="label"
                          fullWidth
                          startIcon={<CloudUpload />}
                          sx={{height: 56}}>
                          {thumbnailPreview
                            ? 'Change Thumbnail'
                            : 'Upload Thumbnail'}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleThumbnailChange}
                          />
                        </MDButton>
                        {thumbnailPreview && (
                          <Box mt={1} position="relative">
                            <img
                              src={thumbnailPreview}
                              alt="Thumbnail"
                              style={{maxHeight: 100, maxWidth: '100%'}}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (thumbnailPreview.startsWith('blob:')) {
                                  URL.revokeObjectURL(thumbnailPreview);
                                }
                                setThumbnailFile(null);
                                setThumbnailPreview('');
                              }}
                              sx={{position: 'absolute', top: 0, right: 0}}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Controller
                              name="isSystem"
                              control={control}
                              render={({field}) => (
                                <Switch
                                  checked={field.value || false}
                                  onChange={field.onChange}
                                />
                              )}
                            />
                          }
                          label="System Template (cannot be deleted)"
                        />
                        <FormControlLabel
                          control={
                            <Controller
                              name="isLocked"
                              control={control}
                              render={({field}) => (
                                <Switch
                                  checked={field.value}
                                  onChange={field.onChange}
                                />
                              )}
                            />
                          }
                          label="Locked (cannot be modified)"
                        />
                      </Grid>

                      {currentScope === 'global' && (
                        <Grid item xs={12}>
                          <Alert severity="success" sx={{mt: 2}}>
                            <strong>Global Template:</strong> This template will
                            be available on all pages automatically.
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  )}

                  {/* Step 1: Field Builder */}
                  {activeStep === 1 && renderFieldBuilder()}

                  {/* Step 2: Default Content */}
                  {activeStep === 2 && renderDefaultContent()}

                  {/* Step 3: Settings & Validation */}
                  {activeStep === 3 && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Alert severity="info">
                          Global settings that apply to the entire section
                        </Alert>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Padding Top</InputLabel>
                          <Controller
                            name="settingsSchema.padding.top"
                            control={control}
                            defaultValue="medium"
                            render={({field}) => (
                              <Select
                                label="Padding Top"
                                value={field.value || 'medium'}
                                onChange={field.onChange}>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Padding Bottom</InputLabel>
                          <Controller
                            name="settingsSchema.padding.bottom"
                            control={control}
                            defaultValue="medium"
                            render={({field}) => (
                              <Select
                                label="Padding Bottom"
                                value={field.value || 'medium'}
                                onChange={field.onChange}>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Padding Left</InputLabel>
                          <Controller
                            name="settingsSchema.padding.left"
                            control={control}
                            defaultValue="none"
                            render={({field}) => (
                              <Select
                                label="Padding Left"
                                value={field.value || 'none'}
                                onChange={field.onChange}>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Padding Right</InputLabel>
                          <Controller
                            name="settingsSchema.padding.right"
                            control={control}
                            defaultValue="none"
                            render={({field}) => (
                              <Select
                                label="Padding Right"
                                value={field.value || 'none'}
                                onChange={field.onChange}>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Margin Top</InputLabel>
                          <Controller
                            name="settingsSchema.margin.top"
                            control={control}
                            defaultValue="none"
                            render={({field}) => (
                              <Select
                                label="Margin Top"
                                value={field.value || 'none'}
                                onChange={field.onChange}>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Margin Bottom</InputLabel>
                          <Controller
                            name="settingsSchema.margin.bottom"
                            control={control}
                            defaultValue="none"
                            render={({field}) => (
                              <Select
                                label="Margin Bottom"
                                value={field.value || 'none'}
                                onChange={field.onChange}>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Background Color"
                          {...register('settingsSchema.bgColor')}
                          defaultValue="#ffffff"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Text Color"
                          {...register('settingsSchema.textColor')}
                          defaultValue="#000000"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="color"
                          label="Accent Color"
                          {...register('settingsSchema.accentColor')}
                          defaultValue="#1976d2"
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Text Alignment</InputLabel>
                          <Controller
                            name="settingsSchema.textAlign"
                            control={control}
                            defaultValue="left"
                            render={({field}) => (
                              <Select
                                label="Text Alignment"
                                value={field.value || 'left'}
                                onChange={field.onChange}>
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="center">Center</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Font Size (px)"
                          {...register('settingsSchema.fontSize')}
                          defaultValue={16}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Container Width</InputLabel>
                          <Controller
                            name="settingsSchema.containerWidth"
                            control={control}
                            defaultValue="full"
                            render={({field}) => (
                              <Select
                                label="Container Width"
                                value={field.value || 'full'}
                                onChange={field.onChange}>
                                <MenuItem value="full">Full Width</MenuItem>
                                <MenuItem value="boxed">Boxed</MenuItem>
                                <MenuItem value="narrow">Narrow</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Max Width"
                          {...register('settingsSchema.maxWidth')}
                          placeholder="1200px"
                          defaultValue="1200px"
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Border Radius</InputLabel>
                          <Controller
                            name="settingsSchema.borderRadius"
                            control={control}
                            defaultValue="none"
                            render={({field}) => (
                              <Select
                                label="Border Radius"
                                value={field.value || 'none'}
                                onChange={field.onChange}>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                                <MenuItem value="circle">Circle</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Box Shadow</InputLabel>
                          <Controller
                            name="settingsSchema.shadow"
                            control={control}
                            defaultValue="none"
                            render={({field}) => (
                              <Select
                                label="Box Shadow"
                                value={field.value || 'none'}
                                onChange={field.onChange}>
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Custom CSS Class"
                          {...register('settingsSchema.customClass')}
                          placeholder="section-custom-class"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Custom CSS"
                          {...register('settingsSchema.customCSS')}
                          placeholder="/* Add custom CSS here */"
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Divider sx={{my: 3}} />

                  <Box display="flex" justifyContent="space-between">
                    <MDButton
                      type="button"
                      variant="outlined"
                      color="secondary"
                      onClick={() => navigate('/section-templates')}>
                      Cancel
                    </MDButton>
                    <Box display="flex" gap={1}>
                      {activeStep > 0 && (
                        <MDButton
                          type="button"
                          color="secondary"
                          variant="outlined"
                          onClick={() => setActiveStep(activeStep - 1)}>
                          Back
                        </MDButton>
                      )}
                      {activeStep < steps.length - 1 ? (
                        <MDButton
                          type="button"
                          variant="gradient"
                          color="info"
                          onClick={() => {
                            if (activeStep === 0) {
                              const scope = getValues('scope');
                              const pageIds = getValues('allowedPageIds');
                              if (
                                scope === 'page-specific' &&
                                (!Array.isArray(pageIds) ||
                                  pageIds.filter(Boolean).length === 0)
                              ) {
                                Notify.failure(
                                  'Select at least one page for a page-specific template',
                                  {
                                    timeout: 4000,
                                    position: 'center-top',
                                  },
                                );
                                return;
                              }
                            }
                            setActiveStep(activeStep + 1);
                          }}>
                          Next
                        </MDButton>
                      ) : (
                        <MDButton
                          type="button"
                          variant="gradient"
                          color="success"
                          onClick={handleSubmit(onSubmit)}
                          startIcon={<Save />}
                          disabled={countriesLoading || loadingSlots}>
                          {countriesLoading || loadingSlots ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : isEditing ? (
                            'Update Template'
                          ) : (
                            'Create Template'
                          )}
                        </MDButton>
                      )}
                    </Box>
                  </Box>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default SectionTemplateForm;